/**
 * App-consistent modal dialog to replace window.confirm().
 *
 * Usage:
 *   import { ConfirmProvider, useConfirm } from '../lib/confirm';
 *   const confirm = useConfirm();
 *   const yes = await confirm('Liquidate this thesis?', { danger: true });
 */
import { createContext, useCallback, useContext, useRef, useState } from 'react';

interface ConfirmOpts {
  danger?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
}

type ConfirmFn = (message: string, opts?: ConfirmOpts) => Promise<boolean>;

const Ctx = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<{
    message: string;
    opts: ConfirmOpts;
  } | null>(null);
  const resolveRef = useRef<((v: boolean) => void) | null>(null);

  const confirm: ConfirmFn = useCallback((message, opts = {}) => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setState({ message, opts });
    });
  }, []);

  function respond(value: boolean) {
    resolveRef.current?.(value);
    resolveRef.current = null;
    setState(null);
  }

  return (
    <Ctx.Provider value={confirm}>
      {children}
      {state && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="card p-5 max-w-md w-full mx-4">
            <div className="text-sm mb-4 whitespace-pre-wrap">{state.message}</div>
            <div className="flex justify-end gap-2">
              <button className="btn" onClick={() => respond(false)}>
                {state.opts.cancelLabel ?? 'Cancel'}
              </button>
              <button
                className={state.opts.danger ? 'btn-danger' : 'btn-primary'}
                onClick={() => respond(true)}
                autoFocus
              >
                {state.opts.confirmLabel ?? 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}

// Graceful fallback: when no provider is mounted (page-level vitest
// rendering, FocalCard preview, etc.) we auto-confirm. Tests can still
// stub `useConfirm` for behaviour assertions.
const CONFIRM_STUB: ConfirmFn = async () => true;

export function useConfirm(): ConfirmFn {
  return useContext(Ctx) ?? CONFIRM_STUB;
}
